/**
 * What does not fit: every record on a graph that says a node is out of place.
 *
 * Its own module, and a LEAF — it imports `./types` and `./panel` and nothing
 * else — because two readers must see one definition and one of them is the
 * coach (`cues.ts`), which the store imports and which therefore may reach
 * nothing that could reach the store. The other is the conjecture pass, whose
 * WHAT DOES NOT FIT block is built from these rows, and the run policy's
 * `conjectureCandidate` counts them. Three readers, one list: a cue saying a
 * record is unexplained and a pass that was never shown it would be two
 * definitions of "does not fit".
 */

import type { Session, StanceTally } from './types'
import { auditLive, kindChannelOf, stanceTally, verificationLive } from './types'
import { panelSplit } from './panel'

/** Which record makes a node an anomaly. */
export type AnomalyKind =
  | 'refuted-outcome'
  | 'refuted-source'
  | 'mixed'
  | 'panel-split'
  | 'audit-objection'
  | 'contradiction'
  | 'gap'

export interface AnomalyRecord {
  nodeId: string
  kind: AnomalyKind
  /** When the record that makes this an anomaly was written. Epoch ms. */
  at: number
  label: string
  claim: string
  /** The auditing model, for an audit objection. */
  auditor?: string
  /**
   * How a mixed check's kept sources sided, for a mixed row and no other: the two
   * counts of `stanceTally`, the one count every surface reads (D262), that tell
   * its four shapes apart. A mixed reading alone does not say that sources
   * disagreed: a refutation the gate demoted can have no side but disputes, a
   * check with no dispute can land there for another reason, and a passage from
   * the person's documents takes no side (D325).
   */
  sides?: Pick<StanceTally, 'supports' | 'disputes'>
}

/** Which way a mixed check's sources went: every side against the claim, every
 *  side with it, both sides, or no side recorded. */
export type MixedShape = 'against' | 'with' | 'split' | 'unsided'

/**
 * A mixed row's shape, from its `sides`: `mixedNote`'s four cases, read off the
 * same count. Decided here once for both readers of the rows that word it, the
 * conjecture pass's block and the coach's `unexplained` cue, so the two cannot
 * describe one check two ways (D325).
 */
export function mixedShape(sides: AnomalyRecord['sides']): MixedShape {
  const { supports = 0, disputes = 0 } = sides ?? {}
  if (disputes > 0 && supports === 0) return 'against'
  if (supports > 0 && disputes === 0) return 'with'
  if (supports > 0 && disputes > 0) return 'split'
  return 'unsided'
}

/**
 * Every record on the graph that says something does not fit, newest first.
 *
 * Assembled from RECORDS — an outcome, a check, a panel's votes, an audit's
 * verdict, and the node kinds a contradiction or gap is — and never from a
 * self-rating. The one self-rating that reaches it is `tension` through
 * `kindChannelOf`, where it counts as a channel and never as a magnitude, exactly
 * as every other consumer of that function counts it.
 *
 * A node can carry several anomalies at once; each is its own row, because a
 * refuted outcome and a split panel on one claim are two facts. A dead end the
 * model closed without a result is left out, as `buildContext` leaves it out of
 * "open contradictions": it is closed, not unexplained.
 */
export function anomalyRecords(session: Session): AnomalyRecord[] {
  const out: AnomalyRecord[] = []
  for (const id of session.order) {
    const n = session.nodes[id]
    if (!n || n.parentId === null) continue
    if (n.terminal && !n.outcome) continue
    const row = (
      kind: AnomalyKind,
      at: number,
      more: Pick<AnomalyRecord, 'auditor' | 'sides'> = {},
    ) =>
      out.push({
        nodeId: n.id,
        kind,
        at,
        label: n.label,
        claim: n.claim,
        ...(more.auditor ? { auditor: more.auditor } : {}),
        ...(more.sides ? { sides: more.sides } : {}),
      })
    if (n.outcome?.bearing === 'refuted') row('refuted-outcome', n.outcome.recordedAt)
    if (n.verification && verificationLive(n)) {
      if (n.verification.reading === 'refuted') row('refuted-source', n.verification.checkedAt)
      else if (n.verification.reading === 'mixed') {
        const { supports, disputes } = stanceTally(n.verification.sources)
        row('mixed', n.verification.checkedAt, { sides: { supports, disputes } })
      }
    }
    if (n.panel && panelSplit(n.panel)) row('panel-split', n.panel.checkedAt)
    if (n.audit && auditLive(n) && n.audit.verdict === 'objection')
      row('audit-objection', n.audit.at, { auditor: n.audit.model.name })
    const ch = kindChannelOf(n)
    if (ch === 'tension') row('contradiction', n.createdAt)
    else if (ch === 'gap' && n.kind === 'gap') row('gap', n.createdAt)
  }
  return out.sort((a, b) => b.at - a.at || a.nodeId.localeCompare(b.nodeId))
}
